// <copyright file="MarkerClusterGroup.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable @typescript-eslint/no-explicit-any */

import { createPathComponent } from "@react-leaflet/core";
import { default as LEAF } from "leaflet";
import type { MarkerClusterGroupOptions } from "leaflet";
import "leaflet.markercluster";

/**
 * Leaflet marker cluster group component with properties and event listeners
 * inherited from the parent component.
 */
const MarkerClusterGroup = createPathComponent(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ children: _c, ...props }, ctx) => {
        const clusterEvents: Map<string, any> = new Map<string, any>();
        const markerClusterOpts: MarkerClusterGroupOptions = {
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: true,
            zoomToBoundsOnClick: true,
            disableClusteringAtZoom: 14,
            maxClusterRadius: 40,
        };

        // Parse inherited properties and event listeners
        Object.entries(props).forEach(([key, value]) => {
            if (key.toString().startsWith("on")) {
                clusterEvents.set(key, value);
            } else {
                // Cast to any to set a marker options property by key
                (markerClusterOpts as any)[key] = value;
            }
        });

        // Create markerClusterGroup Leaflet element
        const markerClusterGroup = LEAF.markerClusterGroup(markerClusterOpts);

        // Initialize event listeners
        Object.entries(clusterEvents).forEach(([eventAsProp, callback]) => {
            const clusterEvent = `cluster${eventAsProp
                .substring(2)
                .toLowerCase()}`;
            markerClusterGroup.on(clusterEvent, callback);
        });

        return {
            instance: markerClusterGroup,
            context: { ...ctx, layerContainer: markerClusterGroup },
        };
    }
);

export default MarkerClusterGroup;
